import SeoMeta from 'components/Basic/Head/SeoMeta';
import Adverts from 'components/Blocks/Adverts/Adverts';
import LastVisitedProducts from 'components/Blocks/Product/LastVisitedProducts/LastVisitedProducts';
import { SkeletonManager } from 'components/Blocks/Skeleton/SkeletonManager';
import Footer from 'components/Layout/Footer/Footer';
import FooterBigIcons from 'components/Layout/Footer/FooterBigIcons/FooterBigIcons';
import { NewsletterFormFooterBlock } from 'components/Layout/Footer/NewsletterForm/NewsletterFormFooterBlock';
import { AccessibilityNavigation } from 'components/Layout/Header/AccessibilityNavigation/AccessibilityNavigation';
import { Header } from 'components/Layout/Header/Header';
import NotificationBars from 'components/Layout/NotificationBars/NotificationBars';
import { Webline } from 'components/Layout/Webline/Webline';
import { HreflangLinkApi } from 'graphql/generated';
import useTranslation from 'next-translate/useTranslation';
import { useSessionStore } from 'store/useSessionStore';
import { OgTypeEnum } from 'types/seo';

type LayoutProps = {
    title?: string | null;
    description?: string | null;
    showLastVisitedProducts?: boolean;
    hreflangLinks?: HreflangLinkApi[];
    isFetchingData?: boolean;
    withoutMoveUpActionButton?: boolean;
    ogType?: OgTypeEnum | undefined;
    ogImageUrlDefault?: string | null | undefined;
    robotsMetaTag?: string | null;
};

/**
 * Basic page layout for common pages
 */
const CommonLayout: FC<LayoutProps> = ({
    children,
    title,
    description,
    showLastVisitedProducts,
    hreflangLinks,
    isFetchingData,
    withoutMoveUpActionButton,
    ogType,
    ogImageUrlDefault,
    robotsMetaTag,
}) => {
    const { isPageLoading, redirectPageType } = useSessionStore((s) => s);
    const { t } = useTranslation();

    return (
        <>
            <SeoMeta
                defaultHreflangLinks={hreflangLinks}
                description={description}
                ogImageUrlDefault={ogImageUrlDefault}
                ogType={ogType}
                robotsMetaTag={robotsMetaTag}
                title={title}
            />
            <AccessibilityNavigation />
            <NotificationBars />
            <Header simple={redirectPageType === 'transport-and-payment'} />
            <main
                aria-label={title ? t('Main content: {{pageTitle}}', { pageTitle: title }) : t('Main content')}
                id="main-content"
            >
                <Adverts withGapBottom withWebline positionName="header" />
                <SkeletonManager isFetchingData={isFetchingData}>{children}</SkeletonManager>
                {showLastVisitedProducts && !isPageLoading && !isFetchingData && <LastVisitedProducts />}
            </main>
            <Webline>
                <FooterBigIcons />
            </Webline>
            <NewsletterFormFooterBlock />
            <Webline type="light">
                <Footer withoutMoveUpActionButton={withoutMoveUpActionButton} />
            </Webline>
        </>
    );
};

/* @component */
export default CommonLayout;
